import { Lightbulb, X } from 'lucide-react';
import { Button } from './ui/button';
import { useFirstTimeHint } from '../hooks/useFirstTimeHint';

/**
 * Banner contextual de primera visita que aparece DENTRO de la página
 * (no es un popover) la primera vez que el usuario abre esa pantalla.
 *
 * - `hintId` único por página (ej. 'flujo-caja-celda-editable').
 * - Persiste en localStorage `chirino-hints-seen:<email>`.
 * - Se cierra con la X o con "Entendido".
 *
 * Uso:
 *   <FirstTimeHint
 *     hintId="flujo-caja-celda-editable"
 *     title="Tip rápido"
 *     description="Haz clic en cualquier celda del grid para editar la provisión."
 *   />
 *
 * También admite children para layouts más complejos:
 *   <FirstTimeHint hintId="cxp-nominas-mover" title="Tip">
 *     Puedes <strong>arrastrar</strong> facturas entre nóminas para reorganizar.
 *   </FirstTimeHint>
 */
export default function FirstTimeHint({
  hintId,
  title,
  description,
  children,
  variant = 'info', // info | success | warning
  className = '',
}) {
  const { visible, dismiss } = useFirstTimeHint(hintId);

  if (!visible) return null;

  const styles = {
    info:    { ring: 'ring-blue-200 dark:ring-blue-900/40',    bg: 'bg-blue-50 dark:bg-blue-950/30',    icon: 'text-blue-600 dark:text-blue-400',    btn: 'bg-blue-600 hover:bg-blue-700' },
    success: { ring: 'ring-emerald-200 dark:ring-emerald-900/40', bg: 'bg-emerald-50 dark:bg-emerald-950/30', icon: 'text-emerald-600 dark:text-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    warning: { ring: 'ring-amber-200 dark:ring-amber-900/40',  bg: 'bg-amber-50 dark:bg-amber-950/30',  icon: 'text-amber-600 dark:text-amber-400',  btn: 'bg-amber-600 hover:bg-amber-700' },
  }[variant] || { ring: 'ring-blue-200', bg: 'bg-blue-50', icon: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' };

  return (
    <div
      className={`relative rounded-xl ${styles.bg} ring-1 ${styles.ring} p-3.5 pr-10 flex gap-3 items-start animate-in fade-in slide-in-from-top-2 duration-500 ${className}`}
      data-testid={`hint-${hintId}`}
      role="status"
    >
      <Lightbulb className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} strokeWidth={2} />
      <div className="flex-1 text-sm">
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        {description && <p className="text-muted-foreground leading-relaxed">{description}</p>}
        {children}
        <Button
          size="sm"
          onClick={dismiss}
          className={`mt-2 h-7 text-xs text-white ${styles.btn}`}
          data-testid={`hint-${hintId}-dismiss`}
        >
          Entendido
        </Button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Cerrar"
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`hint-${hintId}-close`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
